import React, { memo, KeyboardEvent } from 'react';
import { ChecklistItem } from '../../types';

// ============================================
// Types
// ============================================

interface TaskChecklistProps {
  items: ChecklistItem[];
  onToggle: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: () => void;
  newItemText: string;
  onNewItemChange: (text: string) => void;
  disabled?: boolean;
}

interface ChecklistProgressProps {
  completed: number;
  total: number;
  size?: 'sm' | 'md';
}

// ============================================
// Checklist Progress Badge
// ============================================

export const ChecklistProgress: React.FC<ChecklistProgressProps> = memo(({ 
  completed, 
  total,
  size = 'sm',
}) => {
  if (total === 0) return null;
  
  const isComplete = completed === total;
  const sizeClasses = size === 'sm'
    ? 'px-1.5 py-0.5 text-[8px] md:text-[9px]'
    : 'px-2 py-1 text-[10px]';
  
  return (
    <span 
      className={`
        flex items-center gap-1 rounded-lg border font-black uppercase tracking-widest
        ${sizeClasses}
        ${isComplete 
          ? 'bg-emerald-50 text-emerald-600 border-emerald-100' 
          : 'bg-slate-50 text-slate-500 border-slate-100'
        }
      `}
    >
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      {completed}/{total}
    </span>
  );
});

ChecklistProgress.displayName = 'ChecklistProgress';

// ============================================
// Checklist Item Component
// ============================================

interface ChecklistItemRowProps {
  item: ChecklistItem;
  onToggle: () => void;
  onRemove: () => void;
  disabled?: boolean;
}

const ChecklistItemRow: React.FC<ChecklistItemRowProps> = memo(({ 
  item, 
  onToggle, 
  onRemove,
  disabled = false,
}) => {
  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100 group">
      <button 
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`
          w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all
          disabled:opacity-50 disabled:cursor-not-allowed
          ${item.completed 
            ? 'bg-indigo-600 border-indigo-600 text-white' 
            : 'bg-white border-slate-300 hover:border-indigo-400'
          }
        `}
        aria-label={item.completed ? 'Mark as incomplete' : 'Mark as complete'}
      >
        {item.completed && (
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
      
      <span 
        className={`
          flex-1 text-xs md:text-sm font-semibold transition-colors
          ${item.completed ? 'text-slate-400 line-through' : 'text-slate-700'}
        `}
      >
        {item.text}
      </span>
      
      <button 
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="
          text-slate-300 hover:text-rose-500 p-1 
          opacity-0 group-hover:opacity-100 transition-all
          disabled:opacity-50 disabled:cursor-not-allowed
        "
        aria-label="Remove item"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>
  );
});

ChecklistItemRow.displayName = 'ChecklistItemRow';

// ============================================
// Main Checklist Component
// ============================================

export const TaskChecklist: React.FC<TaskChecklistProps> = memo(({
  items,
  onToggle,
  onRemove,
  onAdd,
  newItemText,
  onNewItemChange,
  disabled = false,
}) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div>
      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">
        Checklist
        {items.length > 0 && (
          <span className="ml-2 text-slate-300">
            ({items.filter(i => i.completed).length}/{items.length} complete)
          </span>
        )}
      </label>
      
      {/* Existing Items */}
      {items.length > 0 && (
        <div className="space-y-2 mb-4">
          {items.map(item => (
            <ChecklistItemRow
              key={item.id}
              item={item}
              onToggle={() => onToggle(item.id)}
              onRemove={() => onRemove(item.id)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
      
      {/* Add New Item */}
      <div className="flex gap-2">
        <input 
          type="text" 
          value={newItemText}
          onChange={(e) => onNewItemChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add sub-task..."
          disabled={disabled}
          className="
            flex-1 px-5 py-3 rounded-xl border border-slate-200 bg-slate-50 
            outline-none text-[11px] font-bold
            focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all
          "
        />
        <button 
          type="button"
          onClick={onAdd}
          disabled={disabled || !newItemText.trim()}
          className="
            px-5 py-3 bg-slate-900 text-white 
            font-black uppercase tracking-widest text-[9px] rounded-xl
            hover:bg-slate-800 active:scale-95
            disabled:opacity-50 disabled:cursor-not-allowed
            transition-all
          "
        >
          Add
        </button>
      </div>
    </div>
  );
});

TaskChecklist.displayName = 'TaskChecklist';

export default TaskChecklist;
